import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileCategoriesOpen, setIsMobileCategoriesOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const showSolidBg = !isHomePage || isScrolled;

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  const navTextColor = showSolidBg
    ? 'oklch(var(--foreground-900))'
    : 'oklch(var(--background-50))';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        showSolidBg
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70 shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-10 flex items-center justify-between h-[68px]">
        {/* Logo / Brand */}
        <Link
          to="/"
          className="font-label text-base font-bold whitespace-nowrap tracking-tight hover:opacity-80 transition-opacity cursor-pointer"
          style={{ color: navTextColor }}
        >
          MulebuySpreadsheets
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-6">
          <Link
            to="/"
            className="text-sm font-semibold whitespace-nowrap hover:opacity-75 transition-opacity cursor-pointer"
            style={{ color: navTextColor }}
          >
            <span className="w-4 h-4 inline-flex items-center justify-center mr-1.5">
              <i className="ri-home-4-line text-sm"></i>
            </span>
            Home
          </Link>

          <div ref={dropdownRef} className="relative">
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-foreground-900 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-foreground-800 transition-colors cursor-pointer"
              style={{ color: 'oklch(var(--background-50))' }}
            >
              Categories
              {isDropdownOpen ? (
                <i className="ri-arrow-up-s-line text-sm"></i>
              ) : (
                <i className="ri-arrow-down-s-line text-sm"></i>
              )}
            </button>

            {isDropdownOpen && (
              <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 bg-background-50 rounded-2xl border border-background-200/70 shadow-lg py-2 w-[260px] max-h-[420px] overflow-y-auto z-50">
                {categories.map((cat) => (
                  <a
                    key={cat.slug}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="block px-5 py-3 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors whitespace-nowrap"
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            )}
          </div>

          <Link
            to="/mulebuy-spreadsheet-guide"
            className="text-sm font-semibold whitespace-nowrap hover:opacity-75 transition-opacity cursor-pointer"
            style={{ color: navTextColor }}
          >
            <span className="w-4 h-4 inline-flex items-center justify-center mr-1.5">
              <i className="ri-book-open-line text-sm"></i>
            </span>
            Shopping Guide
          </Link>
        </div>

        {/* CTA Button */}
        <div className="flex items-center gap-3">
          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className="hidden md:flex items-center gap-2 px-5 py-2.5 rounded-full bg-foreground-900 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-foreground-800 transition-colors cursor-pointer"
            style={{ color: 'oklch(var(--background-50))' }}
          >
            <span className="w-5 h-5 rounded-full bg-accent-500 flex items-center justify-center">
              <i className="ri-table-line text-xs" style={{ color: 'oklch(var(--foreground-950))' }}></i>
            </span>
            Mulebuy Spreadsheet
          </a>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg hover:bg-foreground-900/10 transition-colors cursor-pointer"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <i className="ri-close-line text-xl" style={{ color: 'oklch(var(--foreground-900))' }}></i>
            ) : (
              <i className="ri-menu-line text-xl" style={{ color: navTextColor }}></i>
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden fixed inset-0 top-[68px] bg-background-50 z-40 overflow-y-auto">
          <div className="px-6 py-6 space-y-5">
            <Link
              to="/"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center gap-3 text-base font-semibold text-foreground-900 py-2"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-home-4-line text-base"></i>
              </span>
              Home
            </Link>

            <Link
              to="/mulebuy-spreadsheet-guide"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center gap-3 text-base font-semibold text-foreground-900 py-2"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-book-open-line text-base"></i>
              </span>
              Shopping Guide
            </Link>

            {/* Mobile Categories Accordion */}
            <div>
              <button
                onClick={() => setIsMobileCategoriesOpen(!isMobileCategoriesOpen)}
                className="flex items-center justify-between w-full text-base font-semibold text-foreground-900 py-2 cursor-pointer"
              >
                <span className="flex items-center gap-3">
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-grid-line text-base"></i>
                  </span>
                  Categories
                </span>
                <span className="w-5 h-5 flex items-center justify-center transition-transform duration-200" style={{ transform: isMobileCategoriesOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                  <i className="ri-arrow-down-s-line text-base"></i>
                </span>
              </button>

              {isMobileCategoriesOpen && (
                <div className="ml-8 mt-2 space-y-1 pb-2">
                  {categories.map((cat) => (
                    <a
                      key={cat.slug}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 py-2.5 text-sm text-foreground-600 hover:text-foreground-950 transition-colors"
                    >
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-arrow-right-up-line text-xs"></i>
                      </span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <div className="pt-4 border-t border-background-200/70">
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-foreground-900 text-background-50 text-sm font-semibold whitespace-nowrap"
                style={{ color: 'oklch(var(--background-50))' }}
              >
                <span className="w-5 h-5 rounded-full bg-accent-500 flex items-center justify-center">
                  <i className="ri-table-line text-xs" style={{ color: 'oklch(var(--foreground-950))' }}></i>
                </span>
                Mulebuy Spreadsheet
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}